import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import Layout from "../layout";
import axios from "axios";
import Swal from "sweetalert2";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import NativeSelect from "@mui/material/NativeSelect";
import { useSelector } from "react-redux";
import { ROOT_API } from "../../api/config";
import AddressPicker from "../../components/address-picker";
import { formatDate, formatDateTime } from "../../utils/dates";
import "./style.scss";

// Email is available to everyone; SMS / Email+SMS are PREMIUM-only (the notifier gates SMS
// on premium, and these are offered only to premium users below).
const EMAIL_METHOD = { id: "email", name: "Email" };
const PREMIUM_METHODS = [
  EMAIL_METHOD,
  { id: "sms", name: "SMS" },
  { id: "both", name: "Email + SMS" },
];

const fetchSavedAddresses = async (headers) => {
  const res = await axios.get(`${ROOT_API}apis/user/dashboard`, {
    headers,
  });
  return res?.data?.data?.addresses || [];
};

const ServiceRegisterForm = () => {
  const [address, setAddress] = useState(null);
  const [seoGrad, setSeoGrad] = useState("");
  const [method, setMethod] = useState("email");
  const [addressPickerKey, setAddressPickerKey] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [addressLoadFailed, setAddressLoadFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isPremium, setIsPremium] = useState(null); // null = unknown/loading
  const [addressLimit, setAddressLimit] = useState(null);
  const [plan, setPlan] = useState(null); // free | premium | business
  const [upgradeOpen, setUpgradeOpen] = useState(false); // address-cap upgrade popup

  // Pre-seed the address picker from an SEO landing page (/grad/<slug> CTA), one-shot then clear.
  useEffect(() => {
    const slug = localStorage.getItem("seo_grad");
    if (slug) {
      setSeoGrad(slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
      localStorage.removeItem("seo_grad");
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${ROOT_API}apis/billing/status`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        const d = res?.data?.data || {};
        if (alive) {
          setIsPremium(!!d.premium);
          setAddressLimit(typeof d.address_limit === "number" ? d.address_limit : 1);
          setPlan(d.plan || "free");
        }
      } catch (e) {
        if (alive) {
          setIsPremium(false);
          setAddressLimit(1);
          setPlan("free");
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const intl = useIntl();
  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";
  const locale = selectedLocale === "en" ? "en-US" : "hr-HR";

  const addressPreview = address
    ? [address.road, address.house_number, address.place || address.settlement]
        .filter(Boolean)
        .join(", ")
    : "";

  const loadSavedAddresses = async () => {
    setIsLoadingAddresses(true);
    setAddressLoadFailed(false);
    try {
      const nextAddresses = await fetchSavedAddresses({
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      });
      setAddresses(nextAddresses);
    } catch (error) {
      setAddressLoadFailed(true);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  useEffect(() => {
    let alive = true;

    const bootstrapSavedAddresses = async () => {
      setIsLoadingAddresses(true);
      setAddressLoadFailed(false);
      try {
        const nextAddresses = await fetchSavedAddresses({
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        });
        if (alive) {
          setAddresses(nextAddresses);
        }
      } catch (error) {
        if (alive) {
          setAddressLoadFailed(true);
        }
      } finally {
        if (alive) {
          setIsLoadingAddresses(false);
        }
      }
    };

    bootstrapSavedAddresses();

    return () => {
      alive = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    // At the plan's address cap (and not already on the top tier) → offer an upgrade in a
    // popup instead of trying to add another address. The backend also enforces this (402),
    // handled below as a backstop in case the client-side count is stale.
    if (addressLimit != null && addresses.length >= addressLimit && plan !== "business") {
      setUpgradeOpen(true);
      return;
    }
    if (!address || !address.road) {
      Swal.fire({
        icon: "warning",
        title: intl.formatMessage({ id: "select_an_address" }),
        confirmButtonText: "OK",
      });
      return;
    }
    const request = {
      road: address.road,
      house_number: address.house_number,
      settlement: address.settlement,
      latitude: address.latitude,
      longitude: address.longitude,
      osm_id: address.osm_id,
      notification_method: method,
      notify_electricity: true,
      notify_water: true,
    };
    try {
      setIsSubmitting(true);
      const res = await axios.post(
        `${ROOT_API}apis/register-outage-service`,
        request,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (res) {
        await loadSavedAddresses();
        setAddress(null);
        setMethod("email");
        setAddressPickerKey((current) => current + 1);
        Swal.fire({
          icon: "success",
          title: intl.formatMessage({
            id: "register_service_success",
          }),
          text: intl.formatMessage({
            id: "register_service_success_text",
          }),
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      if (error?.response?.status === 402) {
        // Address-cap reached (or premium lapsed mid-session). Re-sync so the limit reflects
        // reality, then offer the upgrade in the popup (don't blindly flip premium off).
        await loadSavedAddresses();
        setUpgradeOpen(true);
        return;
      }
      const code = String(error?.response?.data?.error_code || "");
      const isDuplicate = code === "duplicate" || code.endsWith("01");
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({
          id: isDuplicate ? "register_service_duplicate" : "there_are_error_occured",
        }),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (savedAddress) => {
    if (deletingId) {
      return;
    }
    const confirmed = await Swal.fire({
      icon: "warning",
      title: intl.formatMessage({ id: "register_service_delete_confirm_title" }),
      text: intl.formatMessage({ id: "register_service_delete_confirm_text" }),
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({ id: "register_service_delete_confirm_ok" }),
      cancelButtonText: intl.formatMessage({ id: "register_service_delete_cancel" }),
    });
    if (!confirmed.isConfirmed) {
      return;
    }
    try {
      setDeletingId(savedAddress.id);
      await axios.post(
        `${ROOT_API}apis/delete-outage-service`,
        { id: savedAddress.id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      await loadSavedAddresses();
      Swal.fire({
        icon: "success",
        title: intl.formatMessage({ id: "register_service_delete_success" }),
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "register_service_delete_error" }),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const renderAddresses = () => {
    if (isLoadingAddresses) {
      return (
        <div className="service-register__state">
          {intl.formatMessage({ id: "register_service_saved_loading" })}
        </div>
      );
    }

    if (addressLoadFailed) {
      return (
        <div className="service-register__state service-register__state--error">
          {intl.formatMessage({ id: "register_service_saved_error" })}
        </div>
      );
    }

    if (!addresses.length) {
      return (
        <div className="service-register__state">
          {intl.formatMessage({ id: "dashboard_no_addresses_body" })}
        </div>
      );
    }

    return (
      <div className="service-register__address-list">
        {addresses.map((savedAddress) => (
          <article className="service-register__address-card" key={savedAddress.id}>
            <div className="service-register__address-top">
              <div>
                <h3 className="service-register__address-label">{savedAddress.label}</h3>
                <p className="service-register__address-sub">
                  {intl.formatMessage({ id: "dashboard_watching_since" })}{" "}
                  {formatDate(savedAddress.created_at, locale)}
                </p>
              </div>
              <span
                className={`service-register__badge ${
                  savedAddress.status === "outage_found"
                    ? "service-register__badge--active"
                    : "service-register__badge--quiet"
                }`}
              >
                {intl.formatMessage({
                  id:
                    savedAddress.status === "outage_found"
                      ? "dashboard_status_outage_found"
                      : "dashboard_status_watching",
                })}
              </span>
            </div>

            <div className="service-register__address-meta">
              <span>
                {intl.formatMessage({ id: "dashboard_notification_method" })}:{" "}
                {String(savedAddress.notification_method || "").toUpperCase()}
              </span>
              <span>
                {intl.formatMessage({ id: "dashboard_matches_count" })}:{" "}
                {savedAddress.matches_count}
              </span>
              <span>
                {intl.formatMessage({ id: "dashboard_last_alert" })}:{" "}
                {savedAddress.last_alert_at
                  ? formatDateTime(savedAddress.last_alert_at, locale)
                  : intl.formatMessage({ id: "dashboard_not_sent_yet" })}
              </span>
            </div>

            <div className="service-register__address-actions">
              <button
                type="button"
                className="service-register__address-delete"
                onClick={() => handleDelete(savedAddress)}
                disabled={deletingId === savedAddress.id}
              >
                {intl.formatMessage({
                  id:
                    deletingId === savedAddress.id
                      ? "register_service_submitting"
                      : "register_service_delete",
                })}
              </button>
            </div>
          </article>
        ))}
      </div>
    );
  };

  // Email for everyone; SMS / Email+SMS only for premium.
  const methods = isPremium ? PREMIUM_METHODS : [EMAIL_METHOD];

  return (
    <Layout>
      <div className="general-form service-register">
        <div className="container">
          <div className="general-form-main">
            <form className="general-form-main-form p-36">
              <p className="font-bold font-20 text-center mb-22">
                <FormattedMessage id="receive_notification" />
              </p>
              <AddressPicker
                key={addressPickerKey}
                value={address}
                onChange={setAddress}
                initialQuery={addressPickerKey === 0 ? seoGrad : ""}
              />
              {addressPreview ? (
                <p className="general-form-main-form_hint mt-12">
                  {addressPreview}
                </p>
              ) : null}

              <div className="service-register__channel">
                <span className="service-register__channel-label">
                  {intl.formatMessage({ id: "channel_label" })}
                </span>
                <div className="service-register__channel-opts">
                  {methods.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`service-register__channel-opt${
                        method === item.id ? " is-active" : ""
                      }`}
                      onClick={() => setMethod(item.id)}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                {!isPremium ? (
                  <span className="service-register__channel-hint">
                    {intl.formatMessage({ id: "channel_free_hint" })}
                  </span>
                ) : null}
              </div>
              <button
                onClick={handleSubmit}
                type="button"
                className="btn-cta general-form-main-form_submit mt-40"
                disabled={isSubmitting}
              >
                {intl.formatMessage({
                  id: isSubmitting
                    ? "register_service_submitting"
                    : "register_service_confirm",
                })}
              </button>
            </form>
          </div>

          <section className="service-register__saved">
            <div className="service-register__saved-head">
              <div>
                <p className="service-register__eyebrow">
                  {intl.formatMessage({ id: "dashboard_summary_addresses" })}
                </p>
                <h2 className="service-register__title">
                  {intl.formatMessage({ id: "register_service_saved_title" })}
                </h2>
                <p className="service-register__copy">
                  {intl.formatMessage({ id: "register_service_saved_copy" })}
                </p>
              </div>
              <div className="service-register__count">{addresses.length}</div>
            </div>
            {renderAddresses()}
          </section>
        </div>
      </div>

      {/* Address-cap reached → upgrade offer as a popup (not a separate page) */}
      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <section className="service-register__gate service-register__gate--modal">
            <p className="service-register__eyebrow">
              {intl.formatMessage({ id: "premium_eyebrow" })}
            </p>
            <h2 className="service-register__title">
              {intl.formatMessage({ id: "gate_add_title" })}
            </h2>
            <p className="service-register__copy">
              {intl.formatMessage({ id: "gate_add_copy" })}
            </p>
            <div className="service-register__gate-actions">
              <Link to="/pretplata" className="btn-cta">
                {intl.formatMessage({ id: "gate_add_upgrade" })}
              </Link>
              <Link to="/danas" className="service-register__gate-link">
                {intl.formatMessage({ id: "gate_add_browse" })}
              </Link>
            </div>
          </section>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ServiceRegisterForm;
